import { Box2 } from "three";
import { Jaco } from "oc-jaco3d/arrangement/Jaco";
import { v2 } from "oc-threejs/short3";
import * as Maths from "oc-commons/Maths";
import { heightBoxWorldPhysical, JSON_DECIMALS } from "../commons/Utils";
import { getTypeNameOfPart } from "./arrangementFunctions";
import type { Pfosten } from "./Pfosten";
import { BoxRuler } from "../arrangement3d/BoxRuler";
import { Segment } from "./Segment";
import { Arrangement } from "./Arrangement";
import { StücklistenIterator } from "./Stückliste";
import { ZElement } from "./ZElement";

export abstract class Part extends Jaco {
  static CLASS_NAME = "Part";

  isPart = true;
  isUrsprungLinks = true;

  // ----------------------------------------------------------------------------------
  // persistierte Werte - Zustand, werden mit der Konfiguration gespeichert
  // ----------------------------------------------------------------------------------

  /** die vom User gesetzte Wunschbreite, kann von der physikalischen Breite abweichen */
  breite = 0;
  /** die vom User gesetzte Wunschhöhe, kann von der physikalischen Höhe abweichen */
  höhe = 1.8;
  /** -1 = rückt nach hinten auf, +1 = rückt nach vorne auf, 0 = bleibt stehen */
  snap: number | null = null;
  /** gibt an, dass der User die Breite festlegen kann */
  isBreiteKonfigurierbar: boolean | null = null;

  // ----------------------------------------------------------------------------------
  // session Werte - Zustand, werden beim App-Neustart zurückgesetzt
  // ----------------------------------------------------------------------------------

  // ----------------------------------------------------------------------------------
  // abgeleitete Werte - d.h. sind letztlich nur berechnet (in update3d) und gecached, kein Zustand
  // Achtung, diese Werte muss man immer ganz besonders im Auge haben, kann sein,
  // dass sie fälschlich doch als Zustand dienen, und das führt dann zu schwierigen Bugs
  // ----------------------------------------------------------------------------------

  /** das Deltaabstandsmass zum linken bzw. rechten Part */
  gapRechts = 0;
  gapLinks = 0;

  /** angrenzende Nachbar-Elemente / Pfosten / Empty */
  nachbarLinks: Part | Pfosten = null;
  nachbarRechts: Part | Pfosten = null;

  massRuler: BoxRuler = null;

  constructor(parent: Jaco) {
    super(parent);
    this.reset();
  }

  reset() {
    this.breite = 0;
    this.snap = 1;
    this.isBreiteKonfigurierbar = false;
    this.nachbarLinks = null;
    this.nachbarRechts = null;
    this.gapLinks = 0;
    this.gapRechts = 0;
  }

  copy(other: Part) {
    this.breite = other.breite;
    this.höhe = other.höhe;
    this.snap = other.snap;
    this.isBreiteKonfigurierbar = other.isBreiteKonfigurierbar;
    return this;
  }

  get arrangement(): Arrangement {
    const agt = super.arrangement as Arrangement;
    if (!agt.isArrangement) debugger;
    return agt;
  }

  abstract clone(): Part;

  get segment() {
    const parent = this.parent as any as Segment;
    return parent?.isSegment ? parent : null;
  }

  get index(): number {
    const parts = this.segment?.getParts() ?? [];
    return parts.indexOf(this);
  }

  getPartAtRelativeIndex(snapDirection: number): Part | Pfosten | null {
    const segment = this.segment;
    if (segment == null) return null;
    const parts = segment.getParts();
    const index = parts.indexOf(this);
    return index === -1 ? null : parts[index + snapDirection] ?? null;
  }

  getSegmentNachbarn(): Array<Part | Pfosten | null> {
    return [-1, +1].map((dir) => this.getPartAtRelativeIndex(dir));
  }

  getNachbarn(): Array<Part | Pfosten> {
    return [this.nachbarLinks, this.nachbarRechts].filter((n): n is Part | Pfosten => n != null);
  }

  getNachbarElemente(): Array<ZElement> {
    return [this.nachbarLinks, this.nachbarRechts].filter(
      (n) => (n as ZElement)?.isElement
    ) as ZElement[];
  }

  getPfosten(): Pfosten[] {
    return this.getNachbarn().filter((p): p is Pfosten => (p as Pfosten).isPfosten);
  }

  uiRemove() {
    this.unsnapNachbarnForRemove();
    this.segment?.remove(this);
    this.segment?.setVerteilungsmodusIndividuell();
  }

  unsnapNachbarnForRemove() {
    for (const dir of [-1, +1] as const) {
      const nachbar = this.getPartAtRelativeIndex(dir);
      // console.log('unsnapNachbarnForRemove',{dir,same:nachbar?.snap == -dir,nachbar});
      if (nachbar?.snap === -dir) {
        nachbar.snap = (nachbar as any)?.anGrenzpfosten ? dir : 0;
      }
    }
  }

  setKonfigurierbareBreite(breite: number) {
    if (!this.isBreiteKonfigurierbar) {
      throw new Error("breite ist nicht konfigurierbar");
    }
    this.setBreite(breite);
    if (breite < 0.01) {
      this.uiRemove();
    }
  }

  setBreite(breite: number) {
    this.breite = breite;
  }

  toJSON() {
    // super.toJSON() wird nicht benutzt, weil die Info über das Koordinatensystem nicht notwendig ist
    const json: any = {};
    json.type = (this.constructor as any).CLASS_NAME;
    json.breite = Maths.round(this.breite, JSON_DECIMALS);
    json.höhe = Maths.round(this.höhe, JSON_DECIMALS);
    json.snap = this.snap;
    json.isBreiteKonfigurierbar = this.isBreiteKonfigurierbar;
    json.x0 = Maths.round(this.position.x, JSON_DECIMALS);
    return json;
  }

  fromJSON(json: any) {
    // super.fromJSON(json);
    this.breite = json?.breite;
    this.höhe = json?.höhe;
    this.snap = json?.snap;
    this.isBreiteKonfigurierbar = json?.isBreiteKonfigurierbar;
    this.position.x = json.x0;
    return this;
  }

  collectArtikel(iterator: StücklistenIterator) {}

  getMinMaxBox(): Box2 {
    return new Box2(v2(0, 0), v2(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY));
  }

  get heightPhysical(): number {
    return heightBoxWorldPhysical(this);
  }

  get logKey(): string {
    return getTypeNameOfPart(this) + "-" + this.id;
  }

  get anGrenzpfosten(): boolean {
    return this.getPfosten().some((p) => p.isGrenzpfosten);
  }
}
